import { TableChildRow, TableRow } from "@carbon6/c6-frontend-design";
import type {
  MemberRef,
  TrainingGpuMemberQuota,
  TrainingGpuQuotaScheduleEntry,
  TrainingGpuUsageShare,
  TrainingGpuWorkspaceQuota,
  TrainingGpuWorkspaceUsage,
} from "@/api";
import { compareNumericNullLast } from "@/components/table/compareNumeric";
import { resolveWorkspaceDisplayName } from "./gpuQuotaFormat";
import {
  indexGpuQuotaUsage,
  sumRowUsage,
  workspaceRowUsage,
  type GpuQuotaRowUsage,
  type GpuQuotaUsageIndex,
} from "./gpuQuotaUsageModel";

// 쿼터 표의 최상위 행은 두 종류다. 사람 행(선점 요약)과, 어느 사람에게도 속하지 않는 꼬리 행이다.
// 한 사람이 가진 workspace 내역은 사람 행이 가진 하위 행(TableChildRow)이다. 펼치면 c6 이 사람 행
// 바로 아래에 전폭 하위 행으로 그린다. 소스의 행 수와 행 번호는 바뀌지 않는다.
// 컬럼이 키로 쓰는 필드는 base 가 선언한다(c6 DataTableColumn.key 는 행 프로퍼티로 제한된다).
// 나머지 필드는 각 클래스가 가진다. 렌더는 instanceof 로 좁힌다.

// 사람 행의 id 규칙. 소스가 펼침 상태를 이 id 로 보관하므로, 행 밖에서도 id 를 만들 수 있어야 한다.
// 미할당 행은 member 가 없으므로 uuid 대신 고정 id 를 쓴다.
export function memberQuotaRowId(member: MemberRef | null): string {
  return member === null ? "member:unassigned" : `member:${member.uuid}`;
}

type GpuQuotaWorkspaceRowFields = {
  workspaceId: string;
  displayName: string;
  gpuLimit: number | null;
  effectiveGpuLimit: number | null;
  activeGrantGpus: number | null;
  groupGpuMaxLimit: number | null;
  timezone: string | null;
  schedule: readonly TrainingGpuQuotaScheduleEntry[];
  activeGpus: number;
  pendingGpus: number;
  usage: GpuQuotaRowUsage;
};

// 사람 행이 가진 workspace 내역 한 건. 최상위 행이 아니라 하위 행이므로 TableChildRow 를
// 상속한다. TableChildRow 는 표 계약 없이 식별·가시성·선택 자격만 가진다. 부모 행 id 는
// c6 계약이 요구하는 필수 데이터다.
// 한도 요청의 대상이 이 단위이므로 요청 버튼도 이 행에 있다. 사람 행에 합칠 수 없는 값
// (시간대, 스케줄, group 상한)은 이 행에만 있다.
export class GpuQuotaWorkspaceRow extends TableChildRow {
  readonly workspaceId: string;
  readonly displayName: string;
  readonly gpuLimit: number | null;
  readonly effectiveGpuLimit: number | null;
  // 지금 효력이 있는 임시 증액. effectiveGpuLimit 에 이미 들어 있다.
  readonly activeGrantGpus: number | null;
  readonly groupGpuMaxLimit: number | null;
  readonly timezone: string | null;
  readonly schedule: readonly TrainingGpuQuotaScheduleEntry[];
  readonly activeGpus: number;
  readonly pendingGpus: number;
  readonly gpuHours: number;
  readonly cost: number | null;
  readonly sessionCount: number;
  readonly dailyGpuHours: readonly number[];
  readonly usageByType: readonly TrainingGpuUsageShare[];

  constructor(parentRowId: string, fields: GpuQuotaWorkspaceRowFields) {
    super(parentRowId);
    this.workspaceId = fields.workspaceId;
    this.displayName = fields.displayName;
    this.gpuLimit = fields.gpuLimit;
    this.effectiveGpuLimit = fields.effectiveGpuLimit;
    this.activeGrantGpus = fields.activeGrantGpus;
    this.groupGpuMaxLimit = fields.groupGpuMaxLimit;
    this.timezone = fields.timezone;
    this.schedule = fields.schedule;
    this.activeGpus = fields.activeGpus;
    this.pendingGpus = fields.pendingGpus;
    this.gpuHours = fields.usage.gpuHours;
    this.cost = fields.usage.cost;
    this.sessionCount = fields.usage.sessionCount;
    this.dailyGpuHours = fields.usage.dailyGpuHours;
    this.usageByType = fields.usage.byType;
  }

  // 사람 행(member:)·꼬리 행(orphan:)과 접두사로 구분하므로 id 공간이 겹치지 않는다.
  getRowId(): string {
    return `workspace:${this.workspaceId}`;
  }

  canShow(): boolean {
    return true;
  }

  // 쿼터 표에는 선택 UI 가 없다.
  canSelect(): boolean {
    return false;
  }
}

type GpuQuotaRowFields = {
  displayName: string;
  gpuLimit: number | null;
  effectiveGpuLimit: number | null;
  activeGpus: number;
  pendingGpus: number;
  usage: GpuQuotaRowUsage;
};

// 원시 숫자로 정렬하는 컬럼 키. c6 기본 비교는 셀 문자열을 비교한다. 그래서 "1,200" 이 "900"
// 앞에 온다. 이름 컬럼만 기본 비교를 쓴다.
const numericSortKeys = ["activeGpus", "pendingGpus", "gpuHours", "cost", "sessionCount"] as const;
type NumericSortKey = (typeof numericSortKeys)[number];

function isNumericSortKey(columnKey: string): columnKey is NumericSortKey {
  return (numericSortKeys as readonly string[]).includes(columnKey);
}

export abstract class GpuQuotaTableRow extends TableRow {
  readonly displayName: string;
  readonly gpuLimit: number | null;
  readonly effectiveGpuLimit: number | null;
  readonly activeGpus: number;
  readonly pendingGpus: number;
  readonly gpuHours: number;
  readonly cost: number | null;
  readonly sessionCount: number;
  readonly dailyGpuHours: readonly number[];
  readonly usageByType: readonly TrainingGpuUsageShare[];

  protected constructor(fields: GpuQuotaRowFields) {
    super(null);
    this.displayName = fields.displayName;
    this.gpuLimit = fields.gpuLimit;
    this.effectiveGpuLimit = fields.effectiveGpuLimit;
    this.activeGpus = fields.activeGpus;
    this.pendingGpus = fields.pendingGpus;
    this.gpuHours = fields.usage.gpuHours;
    this.cost = fields.usage.cost;
    this.sessionCount = fields.usage.sessionCount;
    this.dailyGpuHours = fields.usage.dailyGpuHours;
    this.usageByType = fields.usage.byType;
  }

  // 검색어가 이 행을 가리키는지 답한다. 대소문자를 구분하지 않는다. 빈 검색어는 모든 행을 통과시킨다.
  matchesQuery(query: string): boolean {
    const needle = query.trim().toLocaleLowerCase();
    if (needle === "") {
      return true;
    }
    return this.searchTexts().some((text) => text.toLocaleLowerCase().includes(needle));
  }

  // 검색이 대조하는 문자열. 하위 클래스가 자기 식별 문자열을 더한다.
  protected searchTexts(): readonly string[] {
    return [this.displayName];
  }

  // 서버에 단가가 없으면 모든 행의 비용이 null 이다. 그때 비용 정렬은 순서를 바꾸지 않는다.
  override compare(other: this, columnKey: string): number {
    if (isNumericSortKey(columnKey)) {
      return compareNumericNullLast(this[columnKey], other[columnKey]);
    }
    return super.compare(other, columnKey);
  }

  // 모든 행을 표시한다. 가시성은 서버 조회가 정한다.
  canShow(): boolean {
    return true;
  }

  // 선택 UI 가 없는 목록이므로 선택 대상이 아니다.
  canSelect(): boolean {
    return false;
  }
}

// 한 사람이 선점한 workspace 들을 접은 행. 하나라도 무제한이면 한도 합은 무제한(null)이다.
// group 상한·시간대·스케줄은 합치면 뜻이 틀려지므로 내역에만 있다.
export class GpuQuotaMemberRow extends GpuQuotaTableRow {
  // null 이면 아무도 선점하지 않은 workspace 를 모은 미할당 행이다.
  readonly member: MemberRef | null;
  readonly isExpanded: boolean;
  readonly workspaces: readonly GpuQuotaWorkspaceRow[];
  // 행 클릭을 받는 콜백. 캐럿 버튼과 목적지(소스 toggleMember)가 같다. 행은 불변 값 객체이므로
  // 소스를 직접 가지지 않고 콜백만 캡처한다.
  private readonly onToggle: (row: GpuQuotaMemberRow) => void;

  constructor(
    fields: GpuQuotaRowFields & {
      member: MemberRef | null;
      isExpanded: boolean;
      workspaces: readonly GpuQuotaWorkspaceRow[];
      onToggle: (row: GpuQuotaMemberRow) => void;
    },
  ) {
    super(fields);
    this.member = fields.member;
    this.isExpanded = fields.isExpanded;
    this.workspaces = fields.workspaces;
    this.onToggle = fields.onToggle;
  }

  getRowId(): string {
    return memberQuotaRowId(this.member);
  }

  // 행의 어느 곳을 눌러도 내역이 열리고 닫힌다. 캐럿처럼 중첩된 인터랙티브 요소 위 클릭은 c6
  // useRowActivation 가드가 거른다. 그래서 두 번 토글되지 않는다. 캐럿은 aria-expanded 를 가진
  // 접근성 컨트롤로 유지한다.
  override get isRowClickable(): boolean {
    return true;
  }

  override onRowClick(): void {
    this.onToggle(this);
  }

  // 이 선언은 접힘·펼침과 무관하다. "이 행이 가진 것" 이지 "지금 그릴 것" 이 아니다(c6 PIT-011).
  override getRowChildren(): readonly GpuQuotaWorkspaceRow[] {
    return this.workspaces;
  }

  // 무엇을 그릴지는 펼침 상태가 따로 정한다. undefined(표 기본값에 위임)가 아니라 이 행이 값을
  // 정한다. 그래서 캐럿으로 접으면 표 기본값과 무관하게 접힌 상태를 유지한다.
  override get isRowChildrenExpanded(): boolean {
    return this.isExpanded;
  }

  // 사람 이름과 그 사람이 선점한 workspace 의 이름·id 로 찾는다. 하나라도 맞으면 사람 행 전체를
  // 남긴다. 내역을 일부만 남기면 사람 행의 합이 내역과 맞지 않는다.
  protected override searchTexts(): readonly string[] {
    return [
      this.displayName,
      ...this.workspaces.flatMap((workspace) => [workspace.displayName, workspace.workspaceId]),
    ];
  }
}

// 사용량에는 있지만 쿼터 응답의 어느 사람에게도 없는 workspace. upstream 은 한도가 없는
// workspace 도 행으로 보낸다. 그래서 이 경우는 "기간 안에 GPU 를 썼지만 그 뒤 upstream 에서
// 삭제된" workspace 뿐이다. 선점자를 알 수 없으므로 사람 행에 붙일 수 없다. 버리면 표의
// GPU-hours 가 요약과 맞지 않으므로 표 끝에 남긴다.
export class GpuQuotaOrphanRow extends GpuQuotaTableRow {
  readonly workspaceId: string;

  constructor(fields: { workspaceId: string; displayName: string; usage: GpuQuotaRowUsage }) {
    super({
      displayName: fields.displayName,
      gpuLimit: null,
      effectiveGpuLimit: null,
      activeGpus: 0,
      pendingGpus: 0,
      usage: fields.usage,
    });
    this.workspaceId = fields.workspaceId;
  }

  getRowId(): string {
    return `orphan:${this.workspaceId}`;
  }

  protected override searchTexts(): readonly string[] {
    return [this.displayName, this.workspaceId];
  }
}

// 사람별 쿼터와 workspace별 사용량을 한 표로 합친다. 사용량은 workspace 단위다. 지난 기간
// 사용량을 지금 선점자에게 귀속시키면 틀리기 때문이다. 그래서 병합 키는 한 단계 안쪽의
// `member.workspaces[].workspace_id` 다. 사람 행의 GPU-hours 는 그 내역의 합이다.
export function createGpuQuotaTableRows(
  memberQuotas: readonly TrainingGpuMemberQuota[],
  usage: TrainingGpuWorkspaceUsage,
  expandedRowIds: ReadonlySet<string>,
  onToggle: (row: GpuQuotaMemberRow) => void,
): GpuQuotaTableRow[] {
  const usageIndex = indexGpuQuotaUsage(usage);
  const memberRows = memberQuotas.map((quota) =>
    createMemberRow(quota, usageIndex, expandedRowIds, onToggle),
  );
  return [...sortMemberRows(memberRows), ...createOrphanRows(memberQuotas, usage, usageIndex)];
}

function createMemberRow(
  quota: TrainingGpuMemberQuota,
  usageIndex: GpuQuotaUsageIndex,
  expandedRowIds: ReadonlySet<string>,
  onToggle: (row: GpuQuotaMemberRow) => void,
): GpuQuotaMemberRow {
  // 내역은 BE 가 준 이름순을 그대로 쓴다. 내역이 비어 있지 않은 것이 계약이다.
  // 부모 행 id 는 하위 행의 필수 데이터다. 하위 행을 사람 행보다 먼저 만들므로 id 규칙 함수로
  // 같은 값을 계산한다.
  const parentRowId = memberQuotaRowId(quota.member);
  const entries = quota.workspaces.map((workspace) => ({
    workspace,
    usage: workspaceRowUsage(usageIndex, workspace.workspace_id),
  }));
  const usages = entries.map((entry) => entry.usage);
  const workspaces = entries.map((entry) =>
    createWorkspaceRow(parentRowId, entry.workspace, entry.usage),
  );
  return new GpuQuotaMemberRow({
    member: quota.member,
    displayName: quota.member?.display_name ?? "미할당",
    gpuLimit: quota.gpu_limit,
    effectiveGpuLimit: quota.effective_gpu_limit,
    activeGpus: quota.active_gpus,
    pendingGpus: quota.pending_gpus,
    usage: sumRowUsage(usageIndex, usages),
    isExpanded: expandedRowIds.has(memberQuotaRowId(quota.member)),
    workspaces,
    onToggle,
  });
}

function createWorkspaceRow(
  parentRowId: string,
  workspace: TrainingGpuWorkspaceQuota,
  // 기간 안에 사용이 없는 workspace 도 내역에 남긴다. 쓰지 않는 한도도 요청 조율의 대상이다.
  // 그때 값은 0 이다.
  usage: GpuQuotaRowUsage,
): GpuQuotaWorkspaceRow {
  return new GpuQuotaWorkspaceRow(parentRowId, {
    workspaceId: workspace.workspace_id,
    // upstream 이 항상 이름을 보내므로 폴백이 필요 없다.
    displayName: workspace.name,
    gpuLimit: workspace.gpu_limit,
    effectiveGpuLimit: workspace.effective_gpu_limit,
    activeGrantGpus: workspace.active_grant_gpus,
    groupGpuMaxLimit: workspace.group_gpu_max_limit,
    timezone: workspace.timezone,
    schedule: workspace.schedule,
    activeGpus: workspace.active_gpus,
    pendingGpus: workspace.pending_gpus,
    usage,
  });
}

function createOrphanRows(
  memberQuotas: readonly TrainingGpuMemberQuota[],
  usage: TrainingGpuWorkspaceUsage,
  usageIndex: GpuQuotaUsageIndex,
): GpuQuotaOrphanRow[] {
  const quotaWorkspaceIds = new Set(
    memberQuotas.flatMap((quota) => quota.workspaces.map((workspace) => workspace.workspace_id)),
  );
  return usage.workspaces
    .filter((entry) => !quotaWorkspaceIds.has(entry.workspace_id))
    .map(
      (entry) =>
        new GpuQuotaOrphanRow({
          workspaceId: entry.workspace_id,
          displayName: resolveWorkspaceDisplayName(entry.display_name, entry.workspace_id),
          usage: workspaceRowUsage(usageIndex, entry.workspace_id),
        }),
    )
    .sort((a, b) => b.gpuHours - a.gpuHours || a.displayName.localeCompare(b.displayName, "ko"));
}

// 지금 GPU 를 점유한 사람을 위에 둔다. 조율 대화는 그 행에서 시작하기 때문이다. 정렬 순서는
// 다음과 같다(안정 정렬).
//   - 점유량.
//   - 점유가 같으면 기간 사용량.
//   - 그것도 같으면 표시명.
// 미할당 행은 사람이 아니므로 정렬하지 않고 항상 마지막에 둔다.
function sortMemberRows(rows: readonly GpuQuotaMemberRow[]): GpuQuotaMemberRow[] {
  return [...rows].sort((a, b) => {
    const aUnassigned = a.member === null;
    const bUnassigned = b.member === null;
    if (aUnassigned !== bUnassigned) {
      return aUnassigned ? 1 : -1;
    }
    return (
      b.activeGpus - a.activeGpus ||
      b.gpuHours - a.gpuHours ||
      a.displayName.localeCompare(b.displayName, "ko")
    );
  });
}
